import { Inject } from '@odinjs/odin';

import { Endpoint, ToastService } from '@philips-emr/hpms-provider-commons';
import { ActionAPI, DeckAPI, FetchAPI, FieldAPI, LegendsAPI, PanelAPI } from '@philips-emr/tws-components';
import { ToastLevel } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnCancel, OnClick, OnClose, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, MountEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { FileService, HttpService } from '@philips-emr/tws-core/lib/services';
import { HttpMethod } from '@philips-emr/tws-core/lib/services/http-service';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { NegotiationContractAttachmentsAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

import { ContractualDraft } from './negotiation-contractual-draft-controller';

const DRAFT = 'minuta-contratual';

@Controller({ fragment: FragmentNames.NEGOTIATION_CONTRACT_ATTACHMENTS })
class NegotiationContractAttachmentsController extends BaseController {

  @Inject(Aliases.ACTION_DOWNLOAD)
  private actionDownload: ActionAPI;
  @Inject(Aliases.ACTION_REQUEST_CHANGE)
  private actionRequestChange: ActionAPI;
  @Inject(Aliases.PANEL_REQUEST_CHANGE)
  private panelRequestChange: PanelAPI;
  @Inject(Aliases.FETCH_ATTACHMENTS)
  private fetchAttachments: FetchAPI;
  @Inject(Aliases.FETCH_ATTACHMENTS_AMENDMENTS)
  private fetchAttachmentsAmendments: FetchAPI;
  @Inject(Aliases.DECK_ATTACHMENTS)
  private deckAttachments: DeckAPI;
  @Inject(Aliases.DECK_ATTACHMENTS_AMENDMENTS)
  private deckAttachmentsAmendments: DeckAPI;
  @Inject(Aliases.LEGENDS)
  private legends: LegendsAPI;

  @OnMount(Aliases.PANEL_AMENDMENTS)
  onMountAttachmentsAmendment(event: MountEvent): void {
    const api = event.target as PanelAPI;
    const route = this.historyService.getCurrentRoute();
    api.setVisible(route.includes('aditamento'));
  }

  @OnMount(Aliases.PANEL_DRAFT)
  onMountAttachmentDraft(event: MountEvent): void {
    const api = event.target as PanelAPI;
    const route = this.historyService.getCurrentRoute();
    api.setVisible(route.includes(DRAFT));
  }

  @OnMount([Aliases.FETCH_ATTACHMENTS, Aliases.FETCH_ATTACHMENTS_AMENDMENTS])
  onMountFetchClauses(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqNegotiation, nrSeqProposal } = this.historyService.getCurrentRouteParams();
    api.setParameters({ nrSeqNegotiation, nrSeqProposal, attachments: true });
    api.forceFetch();
  }

  @OnMount([Aliases.FIELD_COUNTER_OFFER, Aliases.FIELD_COUNTER_OFFER_AMENDMENTS])
  onMountCounterOffer(event: MountEvent): void {
    const api = event.target as FieldAPI;
    const { ieValueCounterProposal } = api.getData() as unknown as ContractualDraft;
    api.setVisible(isEmptyArrayOrObject(ieValueCounterProposal) || ieValueCounterProposal == 'S');
  }

  @OnAfterFetch([Aliases.FETCH_ATTACHMENTS])
  onAfterFetchAttachments(event: FetchEvent): void {
    const data = event.data as ContractualDraft[];
    if (data && !isEmptyArrayOrObject(data)) {
      const { edit } = data[0];
      this.actionRequestChange.setEnabled(edit == 'S');
    }
    this.actionDownload.setEnabled(!isEmptyArrayOrObject(data));
  }

  @OnAfterFetch([Aliases.FETCH_ATTACHMENTS_AMENDMENTS])
  onAfterFetchAttachmentsAmendments(event: FetchEvent): void {
    const data = event.data as ContractualDraft[];
    if (data && !isEmptyArrayOrObject(data)) {
      const { edit } = data[0];
      this.actionRequestChange.setEnabled(edit == 'S');
    } else {
      this.legends.setShowExpression('');
      this.legends.setHideExpression('');
    }
    this.actionDownload.setEnabled(!isEmptyArrayOrObject(data));
  }

  @OnClick(Aliases.ACTION_REQUEST_CHANGE)
  onClickAccept(): void {
    let selectedItens;
    const route = this.historyService.getCurrentRoute();
    if (route.includes(DRAFT)) {
      selectedItens = this.deckAttachments.state.selectedCards;
    } else {
      selectedItens = this.deckAttachmentsAmendments.state.selectedCards;
    }

    if (isEmptyArrayOrObject(selectedItens)) {
      ToastService.open({ message: 'provider.no-file-selected', level: ToastLevel.WARNING });
      return;
    }

    this.panelRequestChange.setVisible(true);
  }

  @OnSubmit(Aliases.FORM_REQUEST_CHANGE)
  onAcceptProposal(event: SubmitEvent): void {
    const { counterOffer } = event.record as ContractualDraft;
    let selectedItens;
    const route = this.historyService.getCurrentRoute();
    if (route.includes(DRAFT)) {
      selectedItens = this.deckAttachments.state.selectedCards;
    } else {
      selectedItens = this.deckAttachmentsAmendments.state.selectedCards;
    }
    const body = { counterOffer, selectedItens };
    const { nrSeqProposal, nrSeqNegotiation } = this.historyService.getCurrentRouteParams();
    HttpService.post(Endpoint.POST.EDIT_NEGOTIATION_CONTRACT_DRAFT, { nrSeqProposal, nrSeqNegotiation, counterOffer }, { body }).then(() => {
      ToastService.open({ message: 'provider.request-sucessfull', level: ToastLevel.SUCCESS });
      this.panelRequestChange.setVisible(false);

      if (route.includes(DRAFT)) {
        this.fetchAttachments.forceFetch();
        this.deckAttachments.unselectAll();
      } else {
        this.fetchAttachmentsAmendments.forceFetch();
        this.deckAttachmentsAmendments.unselectAll();
      }

    }).catch((error) => {
      this.panelRequestChange.setVisible(false);
      ToastService.open(error.body);
    });
  }

  @OnClose(Aliases.MODAL_REQUEST_CHANGE)
  onCloseModalAccept(): void {
    this.panelRequestChange.setVisible(false);
  }

  @OnCancel(Aliases.FORM_REQUEST_CHANGE)
  onCancelFormAccept(): void {
    this.panelRequestChange.setVisible(false);
  }

  @OnClick(Aliases.ACTION_DOWNLOAD)
  onClickDownload(): void {
    let selectedItens;
    const route = this.historyService.getCurrentRoute();
    const { nrSeqProposal, nrSeqNegotiation } = this.historyService.getCurrentRouteParams();

    if (route.includes(DRAFT)) {
      selectedItens = this.deckAttachments.state.selectedCards;
    } else {
      selectedItens = this.deckAttachmentsAmendments.state.selectedCards;
    }

    if (isEmptyArrayOrObject(selectedItens)) {
      ToastService.open({ message: 'provider.no-file-selected', level: ToastLevel.WARNING });
      return;
    }

    selectedItens.forEach(nrSeqItemProposal => {
      HttpService.get<ContractualDraft>(Endpoint.GET.NEGOTIATION_DRAFT, { nrSeqProposal, nrSeqNegotiation, nrSeqItemProposal, 'attachments': true })
        .then((resp) => {
          const dsFile = resp.body?.dsFile;
          FileService.download(Endpoint.GET.NEGOTIATION_DRAFT_ATTACHMENT, dsFile, { nrSeqNegotiation, nrSeqProposal, nrSeqItemProposal },
            HttpMethod.GET)
            .then(() => {
              if (route.includes(DRAFT)) {
                this.deckAttachments.unselectAll();
                return;
              }
              this.deckAttachmentsAmendments.unselectAll();
            })
            .catch(() => ToastService.open({ message: 'provider.error-finding-file', level: ToastLevel.ERROR }));
        }).catch(() => ToastService.open({ message: 'provider.error-finding-file', level: ToastLevel.ERROR }));
    });

  }

}

export { NegotiationContractAttachmentsController };
